import type { Moment } from "moment";
import type { AddressInput } from "../../../clients/graphqlTypes.js";
import type { CustomFieldsFormValues } from "../custom-fields/EditCustomFieldModal.js";
import type { PendingDocShareProjectDraftClient } from "./projectDraft.js";

// Where a quick-accept field's current value comes from, derived by comparing
// it with the draft (the prefill snapshot): "taken" from the shared quote,
// "absent" because the quote had nothing for it, or "changed" by the user.
export type FieldOrigin = "taken" | "absent" | "changed";

type Scalar = string | number | null | undefined;

function normalise(value: Scalar): string {
  return value === null || value === undefined ? "" : String(value).trim();
}

export function deriveFieldOrigin(value: Scalar, origin: Scalar): FieldOrigin {
  if (normalise(value) !== normalise(origin)) return "changed";
  return normalise(origin) === "" ? "absent" : "taken";
}

// Compared on the normalised address fields: re-selecting the same address
// (possibly with another location lookup) still reads as taken.
function addressKey(address: AddressInput | null): string {
  if (!address) return "";
  return [
    address.streetLines1,
    address.postalCode,
    address.city,
    address.countryCode,
  ]
    .map(normalise)
    .join("|");
}

export function deriveAddressOrigin(
  value: AddressInput | null,
  origin: AddressInput | null
): FieldOrigin {
  if (addressKey(value) !== addressKey(origin)) return "changed";
  return origin ? "taken" : "absent";
}

function clientKey(client: PendingDocShareProjectDraftClient): string {
  switch (client.kind) {
    case "matched":
      return `matched:${client.crmOrganisationId}`;
    case "newContact":
      return [
        "newContact",
        client.contactType,
        client.companyName,
        client.firstname,
        client.familyname,
        client.salutation,
        client.email,
      ]
        .map(normalise)
        .join("|");
    case "none":
      return "none";
  }
}

// `alternativeOrigin` is the quote's own contact candidate offered next to a
// CRM match: choosing it still takes the client from the quote.
export function deriveClientOrigin(
  value: PendingDocShareProjectDraftClient,
  origin: PendingDocShareProjectDraftClient,
  alternativeOrigin?: PendingDocShareProjectDraftClient | null
): FieldOrigin {
  const key = clientKey(value);
  const isFromQuote =
    key === clientKey(origin) ||
    (alternativeOrigin ? key === clientKey(alternativeOrigin) : false);

  if (!isFromQuote) return "changed";
  return origin.kind === "none" ? "absent" : "taken";
}

// Id lists (further categories, teams) compare as sets: the same ids in
// another order are not a change.
export function deriveIdListOrigin(
  value: readonly string[],
  origin: readonly string[]
): FieldOrigin {
  const key = (ids: readonly string[]) =>
    Array.from(new Set(ids)).sort().join("|");
  return deriveFieldOrigin(key(value), key(origin));
}

// Dates compare by calendar day, the precision the project stores.
export function deriveDateOrigin(
  value: Moment | null,
  origin: Moment | null
): FieldOrigin {
  const key = (date: Moment | null) => date?.format("YYYY-MM-DD") ?? "";
  return deriveFieldOrigin(key(value), key(origin));
}

// Money compares by amount; an unset budget is empty.
export function deriveMoneyOrigin(
  value: { amount: number } | null | undefined,
  origin: { amount: number } | null | undefined
): FieldOrigin {
  return deriveFieldOrigin(value?.amount, origin?.amount);
}

// A checkbox's draft value is always `false`, so it is "absent" until ticked.
export function deriveBooleanOrigin(value: boolean): FieldOrigin {
  return deriveFieldOrigin(value ? "true" : "", "");
}

// The quote carries no custom-field data, so the block as a whole is
// "absent" until any field holds a value. "Holds a value" is the rule
// `getCustomFieldsSubmitValues` applies, so the hint and the write agree.
export function deriveCustomFieldsOrigin(
  customFields: CustomFieldsFormValues["customFields"]
): FieldOrigin {
  const hasValue = Object.values(customFields)
    .flatMap(fields => Object.values(fields))
    .some(value => value !== undefined && value !== null && value !== "");
  return deriveFieldOrigin(hasValue ? "set" : "", "");
}
